import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Input, NumberInput, Toast } from '@atoms/index';
import { ZoneDrawingMap, ConfirmModal } from '@organisms/index';
import { adminService, type AdminServiceZone, type AdminServiceZoneWithGeoJson, type CreateServiceZoneDto } from '@services/adminService';
import { formatCurrency } from '@/utils/currency';
import type { GeoJsonPolygon } from '@/utils/geojson';

const DEFAULT_MAP_CENTER: [number, number] = [36.99, 35.33];

export const ServiceZonesTab = () => {
  const [zones, setZones] = useState<AdminServiceZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    city: '',
    district: '',
    isActive: true,
    priority: 0,
    estimatedDeliveryMinutes: 30,
    minimumOrderAmount: 50,
    deliveryFee: 5,
  });
  const [polygon, setPolygon] = useState<GeoJsonPolygon | null>(null);
  const [polygonValid, setPolygonValid] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [toast, setToast] = useState<{ isVisible: boolean; message: string; type: 'success' | 'error' | 'info' }>({
    isVisible: false,
    message: '',
    type: 'info',
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; zoneId: string; zoneName: string }>({
    open: false,
    zoneId: '',
    zoneName: '',
  });

  const loadZones = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getServiceZones();
      setZones(data.zones);
    } catch (e) {
      console.error(e);
      setToast({ isVisible: true, message: 'Bölgeler yüklenemedi.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      city: '',
      district: '',
      isActive: true,
      priority: 0,
      estimatedDeliveryMinutes: 30,
      minimumOrderAmount: 50,
      deliveryFee: 5,
    });
    setPolygon(null);
    setPolygonValid(false);
    setModalOpen(true);
  };

  const openEdit = async (zone: AdminServiceZone) => {
    setEditingId(zone.id);
    setForm({
      name: zone.name,
      city: zone.city,
      district: zone.district,
      isActive: zone.isActive,
      priority: zone.priority ?? 0,
      estimatedDeliveryMinutes: zone.estimatedDeliveryMinutes ?? 30,
      minimumOrderAmount: zone.minimumOrderAmount ?? 50,
      deliveryFee: zone.deliveryFee ?? 5,
    });
    setPolygon(null);
    setPolygonValid(false);
    setModalOpen(true);
    try {
      const full = await adminService.getServiceZone(zone.id);
      if (full?.boundaryGeoJson?.coordinates?.[0]) {
        setPolygon({
          type: 'Polygon',
          coordinates: full.boundaryGeoJson.coordinates,
        });
        setPolygonValid(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.city.trim() || !form.district.trim()) {
      setToast({ isVisible: true, message: 'Ad, şehir ve ilçe zorunludur.', type: 'error' });
      return;
    }
    if (!polygon?.coordinates?.[0] || polygon.coordinates[0].length < 3) {
      setToast({ isVisible: true, message: 'Haritada en az 3 nokta ile bölge çizin.', type: 'error' });
      return;
    }
    const ring = polygon.coordinates[0];
    const polygonCoordinates: [number, number][] = ring.map(([lng, lat]) => [lng, lat]);

    setSaveLoading(true);
    try {
      if (editingId) {
        await adminService.updateServiceZone(editingId, {
          name: form.name.trim(),
          city: form.city.trim(),
          district: form.district.trim(),
          isActive: form.isActive,
          priority: form.priority,
          estimatedDeliveryMinutes: form.estimatedDeliveryMinutes,
          minimumOrderAmount: form.minimumOrderAmount,
          deliveryFee: form.deliveryFee,
          polygonCoordinates,
        });
        setToast({ isVisible: true, message: 'Bölge güncellendi.', type: 'success' });
      } else {
        await adminService.createServiceZone({
          name: form.name.trim(),
          city: form.city.trim(),
          district: form.district.trim(),
          polygonCoordinates,
          isActive: form.isActive,
          priority: form.priority,
          estimatedDeliveryMinutes: form.estimatedDeliveryMinutes,
          minimumOrderAmount: form.minimumOrderAmount,
          deliveryFee: form.deliveryFee,
        });
        setToast({ isVisible: true, message: 'Bölge eklendi.', type: 'success' });
      }
      setModalOpen(false);
      loadZones();
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || 'Kaydetme başarısız.';
      setToast({ isVisible: true, message: msg, type: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteConfirm = (zone: AdminServiceZone) => {
    setDeleteConfirm({ open: true, zoneId: zone.id, zoneName: zone.name });
  };

  const handleDelete = async () => {
    try {
      await adminService.deleteServiceZone(deleteConfirm.zoneId);
      setToast({ isVisible: true, message: 'Bölge silindi.', type: 'success' });
      setDeleteConfirm({ open: false, zoneId: '', zoneName: '' });
      loadZones();
    } catch (e) {
      setToast({ isVisible: true, message: 'Silme başarısız.', type: 'error' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Platform Hizmet Bölgeleri</h2>
        <Button onClick={openCreate}>Yeni bölge</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Yükleniyor...</div>
        ) : zones.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Henüz bölge yok. &quot;Yeni bölge&quot; ile ekleyin.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-semibold px-6 py-3">Ad</th>
                  <th className="text-left font-semibold px-6 py-3">Şehir / İlçe</th>
                  <th className="text-left font-semibold px-6 py-3">Durum</th>
                  <th className="text-right font-semibold px-6 py-3">Öncelik</th>
                  <th className="text-right font-semibold px-6 py-3">Min. sipariş</th>
                  <th className="text-right font-semibold px-6 py-3">Teslimat ücreti</th>
                  <th className="text-right font-semibold px-6 py-3">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {zones.map((z) => (
                  <tr key={z.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 font-medium text-gray-900">{z.name}</td>
                    <td className="px-6 py-3 text-gray-600">{z.city} / {z.district}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${z.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {z.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right text-gray-700">{z.priority}</td>
                    <td className="px-6 py-3 text-right text-gray-700">{z.minimumOrderAmount != null ? formatCurrency(Number(z.minimumOrderAmount)) : '—'}</td>
                    <td className="px-6 py-3 text-right text-gray-700">{z.deliveryFee != null ? formatCurrency(Number(z.deliveryFee)) : '—'}</td>
                    <td className="px-6 py-3 text-right">
                      <Button variant="secondary" size="sm" className="mr-2" onClick={() => openEdit(z)}>
                        Düzenle
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDeleteConfirm(z)}>
                        Sil
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">{editingId ? 'Bölge düzenle' : 'Yeni bölge'}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Ad (örn. Adana/Seyhan)"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Adana/Seyhan"
                />
                <Input
                  label="Şehir"
                  value={form.city}
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  placeholder="Adana"
                />
                <Input
                  label="İlçe"
                  value={form.district}
                  onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
                  placeholder="Seyhan"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Aktif</label>
                </div>
                <NumberInput
                  label="Öncelik (sayı)"
                  value={form.priority === 0 ? '' : String(form.priority)}
                  onChange={(s) => setForm((f) => ({ ...f, priority: s === '' ? 0 : parseInt(s, 10) || 0 }))}
                />
                <NumberInput
                  label="Tahmini teslimat (dk)"
                  value={form.estimatedDeliveryMinutes === 0 ? '' : String(form.estimatedDeliveryMinutes)}
                  onChange={(s) => setForm((f) => ({ ...f, estimatedDeliveryMinutes: s === '' ? 30 : parseInt(s, 10) || 30 }))}
                />
                <NumberInput
                  label="Min. sipariş tutarı (₺)"
                  value={form.minimumOrderAmount === 0 ? '' : String(form.minimumOrderAmount)}
                  onChange={(s) => setForm((f) => ({ ...f, minimumOrderAmount: s === '' ? 0 : parseFloat(s) || 0 }))}
                />
                <NumberInput
                  label="Teslimat ücreti (₺)"
                  value={form.deliveryFee === 0 ? '' : String(form.deliveryFee)}
                  onChange={(s) => setForm((f) => ({ ...f, deliveryFee: s === '' ? 0 : parseFloat(s) || 0 }))}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Bölge sınırı (haritada çizin)</p>
                <p className="text-xs text-gray-500 mb-2">
                  Aşağıdaki &quot;Serbest Çizim&quot;, &quot;Dikdörtgen&quot; veya &quot;Daire&quot; butonuna tıklayın, sonra haritada bölge sınırını oluşturun. En az 3 nokta gerekir.
                </p>
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <ZoneDrawingMap
                    restaurantLat={DEFAULT_MAP_CENTER[0]}
                    restaurantLng={DEFAULT_MAP_CENTER[1]}
                    initialPolygon={polygon}
                    requireRestaurantInside={false}
                    onPolygonChange={setPolygon}
                    onValidationChange={(valid) => setPolygonValid(valid)}
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setModalOpen(false)}>İptal</Button>
              <Button onClick={handleSave} disabled={saveLoading || !polygonValid}>
                {saveLoading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.open}
        title="Bölgeyi sil"
        description={`"${deleteConfirm.zoneName}" bölgesini silmek istediğinize emin misiniz? Bu bölgeye bağlı restoranlar etkilenebilir.`}
        confirmText="Sil"
        cancelText="Vazgeç"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ open: false, zoneId: '', zoneName: '' })}
      />

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isVisible: false }))}
      />
    </div>
  );
};
